import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { permissionObj } from "../../../../../Helpers/permission";
import { errorMsg } from "../../../../../Apis/functions";
import { TABLES } from "../../../../../Apis/Tables";
import { GetFoodieShiftResourceAssigned } from "../../../../../reduxToolkit/FoodieShift/FoodieShiftApi";
import { DeleteItemsApi } from "../../../../../reduxToolkit/Commons/CommonsApi";
import SelectedDepartmentTotalEmployeesCard
    from "../../../../../components/CustomAvailableAssignedItems/SubComponents/SelectedDepartmentTotalEmployeesCard";
import { FOODIE_SHIFT_RESOURCE, FOODIE_SHIFT_RESOURCE_MODE } from "../Model/Models";
import SelectedCafeteriaCard from "../SubComponents/SelectedCafeteriaCard";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Assigned tab logic for a foodie shift section.
 *  - create (no id): fully client-side; the assigned list is the local section
 *    selection and deletion removes items from it.
 *  - update (id present): the assigned list is read from the server
 *    (GetFoodieShiftResourceAssigned, CustomRead) and deletion is committed on
 *    modal confirm via the generic delete-by-table endpoint (the assigned row
 *    carries its join `id`). Mirrors AttendanceShift AssignedShiftResourceApp.
 *
 * @param {Object} props
 * @param {string} props.resource - FOODIE_SHIFT_RESOURCE type.
 * @param {number|string|null|undefined} props.id - Shift id; absent in create.
 * @param {Array<Object>} props.selected - Section selection list (toRegister source).
 * @param {Function} props.setSelected - Section selection setter.
 * @param {Array} props.selectForDelete - Delete buffer (keyId values).
 * @param {Function} props.setSelectForDelete - Buffer setter.
 * @param {Object} props.results - Server assigned page (update mode).
 * @param {Function} props.setModified - Triggers a refetch of the assigned page.
 * @param {boolean} props.triggerDelete - External (header) bulk-delete trigger.
 * @param {Function} props.setTriggerDelete - Trigger setter.
 * @param {Function} props.setIsAllChecked - Select-all state setter.
 */
export const AssignedShiftResourceApp = ({
                                             resource,
                                             id,
                                             selected,
                                             setSelected,
                                             selectForDelete,
                                             setSelectForDelete,
                                             results,
                                             setModified,
                                             triggerDelete,
                                             setTriggerDelete,
                                             setIsAllChecked,
                                         }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const deletePermission = DEV || permission?.includes(permissionObj?.WEB_FOODIE_SHIFT_DELETE);

    const isCreate = id === null || id === undefined;
    const [deleteModalShow, setDeleteModalShow] = useState(false);

    /**
     * Renders a department assigned card (name + employee count) for the grid.
     * @param {Object} args - Row render args from the table.
     * @returns {JSX.Element}
     */
    const renderDepartmentsItem = ({ item, keyId, onDelete, checkbox, handleCheckboxChange, selected }) => {
        return (
            <SelectedDepartmentTotalEmployeesCard
                keyId={keyId}
                item={{
                    [keyId]: item[keyId],
                    fullName: item?.departmentName,
                    totalEmployees: item?.totalEmployees ?? item?.employeeCount,
                    checked: selected?.includes(item[keyId]),
                    disabled: false,
                }}
                onDelete={onDelete}
                handleCheckboxChange={checkbox ? handleCheckboxChange : undefined}
            />
        );
    }

    /**
     * Renders a cafeteria (diner) assigned card for the grid.
     * @param {Object} args - Row render args from the table.
     * @returns {JSX.Element}
     */
    const renderCafeteriaItems = ({ item, keyId, onDelete, checkbox, handleCheckboxChange, selected }) => {
        return (
            <SelectedCafeteriaCard
                keyId={keyId}
                item={{
                    [keyId]: item[keyId],
                    fullName: item?.cafeteriaName ?? item?.foodieCafeteriaName,
                    checked: selected?.includes(item[keyId]),
                    disabled: false,
                }}
                onDelete={onDelete}
                handleCheckboxChange={checkbox ? handleCheckboxChange : undefined}
            />
        )
    };

    const dynamicConfig = useMemo(() => {
        const mode = FOODIE_SHIFT_RESOURCE_MODE[resource];

        if (resource === FOODIE_SHIFT_RESOURCE.CAFETERIAS) {
            return {
                keyId: 'cafeteriaId',
                model: TABLES.FOODIE_SHIFT_CAFETERIA,
                resourceString: mode,
                labelRowsPerPage: 'diners_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_diner_yet',
                searchLabel: 'search_by_name',
                title_modal: 'remove_diners',
                element_modal: 'diners',
                renderAssignedItem: renderCafeteriaItems,
            };
        }
        if (resource === FOODIE_SHIFT_RESOURCE.DEPARTMENTS) {
            return {
                keyId: 'departmentId',
                model: TABLES.FOODIE_SHIFT_DEPARTMENT,
                resourceString: mode,
                labelRowsPerPage: 'departments_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_department_yet',
                searchLabel: 'search_by_name',
                title_modal: 'remove_departments',
                element_modal: 'departments',
                renderAssignedItem: renderDepartmentsItem,
            };
        }
        if (resource === FOODIE_SHIFT_RESOURCE.USERS) {
            return {
                keyId: isCreate ? 'userId' : 'id',
                model: TABLES.FOODIE_SHIFT_CUSTOM_ACCESS,
                resourceString: mode,
                labelRowsPerPage: 'employees_per_page',
                tableNotInformationLegend: 'you_have_not_assigned_any_employee_yet',
                searchLabel: 'search_by_name_email_or_phone',
                title_modal: 'remove_employees',
                element_modal: 'employees',
            }
        }
    }, [resource, isCreate]);

    /**
     * Assigned (CustomRead) request body. Uses the `searchElement` argument
     * forwarded by getData (not the closure) so reset/clear refetches with the
     * right value; an empty search is sent as `key: null` (backend allows it).
     */
    const buildBody = ({ page, rowsPerPage, searchElement }) => ({
        pagination: { page, size: rowsPerPage },
        model: dynamicConfig.model,
        key: searchElement || null,
        resourceId: id,
        resourceString: dynamicConfig.resourceString,
    });

    useEffect(() => {
        if (triggerDelete === true) {
            handleDelete();
            setTriggerDelete(false);
        }
    }, [triggerDelete]);

    /**
     * Opens the delete confirmation modal. When called with a row, that row is
     * the only item buffered for deletion; otherwise the current buffer is used.
     * @param {Object} [item] - Row to delete.
     */
    const handleDelete = (item) => {
        if (item) setSelectForDelete([item[dynamicConfig.keyId]]);
        setDeleteModalShow(true);
    };

    // Client-side selection helpers (create mode grid).
    const handleCheckboxChange = (e) => {
        const key = e?.target?.[dynamicConfig.keyId];
        const checked = e?.target?.checked;
        setSelectForDelete(prev =>
            checked ? [...new Set([...prev, key])] : prev.filter(k => k !== key)
        );
    };

    /**
     * Toggles the "select all" checkbox: buffers every section item for
     * deletion when checking, clears the buffer when unchecking.
     */
    const handleDeleteAll = () => {
        setIsAllChecked(prev => {
            const next = !prev;
            setSelectForDelete(next ? selected.map(e => e[dynamicConfig.keyId]) : []);
            return next;
        });
    };

    /**
     * Closes the delete modal and clears the delete buffer / select-all state.
     */
    const hide = () => {
        setSelectForDelete([]);
        setIsAllChecked(false);
        setDeleteModalShow(false);
    };

    /**
     * Confirms deletion of the buffered items. In create mode it just drops
     * them from the local section selection; in update mode it commits the
     * delete-by-table call, then removes the matching resources from the
     * selection and refetches the assigned page.
     */
    const onDelete = () => {
        if (selectForDelete.length <= 0) return;

        if (isCreate) {
            setSelected(prev =>
                prev.filter(e => !selectForDelete.includes(e[dynamicConfig.keyId]))
            );
            toast.success(t('has_been_deleted_successfully'));
            hide();
            return;
        }

        dispatch(DeleteItemsApi({ tableName: dynamicConfig.model, body: selectForDelete }))
            .then(({ payload: { data: { success, code } } }) => {
                if (!success) {
                    errorMsg(code);
                    return;
                }
                setModified(true);
                hide();
            });
    };

    return {
        t,
        isCreate,
        deletePermission,
        deleteModalShow,
        dynamicConfig,
        assignedEndPoint: GetFoodieShiftResourceAssigned,
        buildBody,
        handleDelete,
        handleCheckboxChange,
        handleDeleteAll,
        onDelete,
        hide,
    }
}
